import { forwardRef, memo, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import {
	DetailsBox,
	DetailsBoxContent,
	GreyBox,
} from '@dealstack/frontend/shared/components/DocumentViewerV2/common/commonComponents.tsx';
import { DocumentViewerHeader } from '@dealstack/frontend/shared/components/DocumentViewerV2/common/DocumentViewerHeader.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import { Flex, Text } from '@mantine/core';
import { useImageViewerController } from './useImageViewerController.ts';

interface Props {
	className?: string;
	fileName: string;
	file?: Blob | string;
	fileExtension: FileExtension;
	details?: ReactNode;
	showDetailsByDefault?: boolean;
	isLoading?: boolean;
	onBackArrowClick?: () => void;
}

const ImageViewer = forwardRef<HTMLDivElement, Props>(
	(
		{
			className,
			fileName,
			file,
			details,
			isLoading,
			fileExtension,
			showDetailsByDefault = false,
			onBackArrowClick,
		},
		ref
	) => {
		const tCommonUi = useTranslationCommonUi();

		const {
			onScaleChange,
			onDownload,
			setShowDetails,
			showDetails,
			imageSizePercent,
			imageSrcUrl,
		} = useImageViewerController({
			file,
			fileName,
			showDetailsByDefault,
			fileExtension,
		});

		return (
			<Flex
				ref={ref}
				className={className}
				direction="column"
				gap="1rem"
				sx={{ flexGrow: 1, overflow: 'hidden', userSelect: 'none' }}
			>
				<DocumentViewerHeader
					title={fileName}
					onBackArrowClick={onBackArrowClick}
					onUpscale={() => onScaleChange(true)}
					onDownscale={() => onScaleChange(false)}
					onDownload={onDownload}
					onToggleDetails={details ? () => setShowDetails(!showDetails) : undefined}
				/>

				<Flex gap="0.5rem" style={{ flexGrow: 1, overflow: 'hidden' }}>
					<GreyBox
						style={{
							flexGrow: 1,
							display: 'flex',
							justifyContent: 'center',
							overflow: 'auto',
						}}
					>
						{isLoading && <InlineLoader />}

						<ImgWrapper imageSizePercent={imageSizePercent}>
							<StyledImg src={imageSrcUrl} imageSizePercent={imageSizePercent} />
						</ImgWrapper>
					</GreyBox>

					{showDetails && !!details && (
						<DetailsBox>
							<Flex
								justify="space-between"
								p="1rem"
								sx={{
									borderBottom: `1px solid ${defaultTheme.v3.colors.gray[2]}`,
								}}
							>
								<Text size="B5Bold">{tCommonUi('preview_pdf.details')}</Text>
								<Icon
									variant="close-v3"
									size="lg"
									onClick={() => setShowDetails(false)}
								/>
							</Flex>
							<DetailsBoxContent>{details}</DetailsBoxContent>
						</DetailsBox>
					)}
				</Flex>
			</Flex>
		);
	}
);

const ImgWrapper = styled.div<{ imageSizePercent: number }>`
	${({ imageSizePercent }) => css`
		width: ${imageSizePercent}%;
		height: ${imageSizePercent}%;
	`};

	display: flex;
	justify-content: center;
`;

const StyledImg = styled.img<{ imageSizePercent: number }>`
	${({ imageSizePercent }) => css`
		width: ${imageSizePercent}%;
		height: ${imageSizePercent}%;
	`};
	object-fit: contain;
`;
const _ImageViewerExport = memo(ImageViewer);

export { _ImageViewerExport as ImageViewer };
